/**
 * Financial-advisory specific ConditionNode that injects Redux state
 * for leaf values and explanations.
 *
 * This component uses Redux selectors which handle reactivity, so no
 * additional memoization wrapper is needed.
 */
import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  selectLeafValueByName,
  selectLeafValueExplanationByName,
} from "@financial-advisory/data-access/financial-advisory-api-service";
import {
  ConditionNodeData,
  ConditionNode as SharedConditionNode,
} from "@unlikelyai-magic/shared/components";
import { Outcome } from "@unlikelyai-magic/shared/data-access/api";

interface ConditionNodeWithReduxProps {
  id: string;
  data: ConditionNodeData;
  isConnectable: boolean;
}

const ConditionNodeComponent = ({
  id,
  data,
  isConnectable,
}: ConditionNodeWithReduxProps) => {
  // Get leaf value from Redux using the node's label (name)
  // When Redux returns undefined (e.g., after clearing), use UNDECIDED
  // Do NOT fall back to data.value as that creates a circular dependency
  // where stale values persist after clearing
  const reduxLeafValue = useSelector(selectLeafValueByName(data.label));
  const leafValueExplanation = useSelector(
    selectLeafValueExplanationByName(data.label)
  );

  // Inject the Redux values into the data
  // Use useMemo to avoid creating new object on every render unless values change
  const enrichedData = useMemo(
    () => ({
      ...data,
      // Use Redux value if available, otherwise UNDECIDED (not data.value)
      // This ensures clearing Redux state properly resets the visual state
      leafValue: reduxLeafValue ?? Outcome.UNDECIDED,
      leafValueExplanation,
    }),
    [data, reduxLeafValue, leafValueExplanation]
  );

  return (
    <SharedConditionNode
      id={id}
      data={enrichedData}
      isConnectable={isConnectable}
    />
  );
};

/**
 * ConditionNode with Redux integration.
 * Redux selectors (useSelector) handle reactivity, so the component
 * re-renders automatically when selected state changes.
 */
export const ConditionNode = ConditionNodeComponent;
